import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Displays dynamic per-item pricing based on quantity and volume pricing tiers.
 * Updates automatically when quantity changes or cart is updated.
 *
 * @typedef {Object} PriceBreak
 * @property {number} quantity - Minimum quantity for this price tier
 * @property {string} price - Formatted price string
 *
 * @typedef {Object} PricePerItemRefs
 * @property {HTMLElement} [pricePerItemText] - The text element displaying the price
 *
 * @extends {Component<PricePerItemRefs>}
 */
class PricePerItemComponent extends Component {
  /** @type {PriceBreak[]} */
  #priceBreaks = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#parsePriceBreaks();
    this.#attachEventListeners();
    this.#updatePriceDisplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Parses price breaks from data attributes.
   */
  #parsePriceBreaks() {
    const minQuantity = parseInt(this.dataset.minQuantity || '') || 1;
    const { variantPrice, priceBreaks: priceBreaksData } = this.dataset;

    if (variantPrice) {
      this.#priceBreaks.push({
        quantity: minQuantity,
        price: variantPrice,
      });
    }

    if (priceBreaksData) {
      const breaks = JSON.parse(priceBreaksData);

      for (const { quantity, price } of breaks) {
        if (quantity && price) {
          this.#priceBreaks.push({
            quantity: parseInt(quantity),
            price,
          });
        }
      }
    }

    this.#priceBreaks.sort((a, b) => b.quantity - a.quantity);
  }

  /**
   * Attaches event listeners for quantity and cart updates.
   */
  #attachEventListeners() {
    const { signal } = this.#abortController;

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      { signal }
    );
  }

  /**
   * Handles quantity selector updates.
   *
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');

    if (
      !form ||
      !(event.target instanceof Node) ||
      !form.contains(event.target)
    ) {
      return;
    }

    this.#updatePriceDisplay();
  };

  /**
   * Handles cart updates.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(() => this.#updatePriceDisplay())
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[price-per-item] Cart update promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Gets the total quantity, including any quantity already in the cart.
   *
   * @returns {number}
   */
  #getCurrentQuantity() {
    const form = this.closest('product-form-component');

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input[name="quantity"]')
    );

    if (!quantityInput) {
      return 1;
    }

    const cartQuantity =
      parseInt(quantityInput.getAttribute('data-cart-quantity') || '0') || 0;

    const inputQuantity = parseInt(quantityInput.value) || 1;

    return cartQuantity + inputQuantity;
  }

  /**
   * Updates the per-item price display.
   */
  updatePriceDisplay() {
    if (!this.#priceBreaks.length || !this.refs.pricePerItemText) {
      return;
    }

    const quantity = this.#getCurrentQuantity();

    const priceBreak =
      this.#priceBreaks.find((priceTier) => quantity >= priceTier.quantity) ||
      this.#priceBreaks[this.#priceBreaks.length - 1];

    if (priceBreak) {
      this.refs.pricePerItemText.innerHTML =
        `${this.dataset.atText} ${priceBreak.price}/${this.dataset.eachText}`;
    }
  }

  /**
   * Private wrapper for event handlers.
   */
  #updatePriceDisplay = () => {
    this.updatePriceDisplay();
  };
}

if (!customElements.get('price-per-item')) {
  customElements.define('price-per-item', PricePerItemComponent);
}
